import type { PixelBuffer } from '../filters';
import type { Layer, LayerMask, Rect, Size } from './types';

/**
 * Layer mask maths — pure, so mask behaviour is unit-tested rather than
 * eyeballed on a canvas.
 *
 * A mask's coverage lives in its **alpha channel**: 255 = the layer shows, 0 = it
 * is hidden. Everything here works on plain `PixelBuffer`s, the same shape the
 * filters use, which is what lets `io` decode once and reuse these functions for
 * inverting, generating and baking masks.
 */

export type MaskMode = 'reveal' | 'hide';

/** The mask that is actually in force, or null when there is nothing to apply. */
export function activeMask(layer: Layer | undefined): LayerMask | null {
  if (!layer?.mask?.enabled || !layer.mask.src) return null;
  return layer.mask;
}

export function hasMask(layer: Layer | undefined): boolean {
  return Boolean(layer?.mask?.src);
}

/**
 * Multiply a layer's alpha by a mask's coverage.
 *
 * Colour is untouched — a mask only ever changes *how much* of a layer shows,
 * never what colour it is. Sizes are allowed to differ (a mask decoded at a
 * different size than the layer it guards) and are sampled with nearest
 * neighbour, which is the honest interpretation of "stretch the stencil".
 */
export function applyMaskToPixels(
  layer: PixelBuffer,
  mask: PixelBuffer,
  options: { inverted?: boolean } = {},
): PixelBuffer {
  const inverted = options.inverted === true;
  const out = new Uint8ClampedArray(layer.data.length);
  for (let y = 0; y < layer.height; y += 1) {
    // Nearest neighbour in the mask's own coordinate space.
    const my = Math.min(mask.height - 1, Math.floor((y * mask.height) / layer.height));
    for (let x = 0; x < layer.width; x += 1) {
      const mx = Math.min(mask.width - 1, Math.floor((x * mask.width) / layer.width));
      const from = (y * layer.width + x) * 4;
      const coverage = mask.data[(my * mask.width + mx) * 4 + 3] ?? 0;
      const factor = (inverted ? 255 - coverage : coverage) / 255;
      out[from] = layer.data[from] ?? 0;
      out[from + 1] = layer.data[from + 1] ?? 0;
      out[from + 2] = layer.data[from + 2] ?? 0;
      out[from + 3] = (layer.data[from + 3] ?? 0) * factor;
    }
  }
  return { data: out, width: layer.width, height: layer.height };
}

/** Flip a mask's coverage. Colour is irrelevant to a mask, so it is zeroed. */
export function invertMaskPixels(mask: PixelBuffer): PixelBuffer {
  const out = new Uint8ClampedArray(mask.data.length);
  for (let i = 0; i < mask.data.length; i += 4) {
    out[i] = 0;
    out[i + 1] = 0;
    out[i + 2] = 0;
    out[i + 3] = 255 - (mask.data[i + 3] ?? 0);
  }
  return { data: out, width: mask.width, height: mask.height };
}

/**
 * A whole-layer mask that shows everything, or hides everything.
 *
 * A revealing mask is opaque **white**: colour is irrelevant to compositing, but a
 * white stencil is what a mask thumbnail has to look like to be readable.
 */
export function solidMaskPixels(size: Size, mode: MaskMode): PixelBuffer {
  const data = new Uint8ClampedArray(size.width * size.height * 4);
  if (mode === 'reveal') {
    for (let i = 0; i < data.length; i += 4) {
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = 255;
    }
  }
  return { data, width: size.width, height: size.height };
}

/**
 * A mask that reveals a rectangle and hides the rest.
 *
 * The rect is in the layer's pixel space and is clamped to it, so a marquee that
 * hangs off the edge produces the mask the user meant rather than an error.
 */
export function rectMaskPixels(rect: Rect, size: Size): PixelBuffer {
  const data = new Uint8ClampedArray(size.width * size.height * 4);
  const left = Math.max(0, Math.floor(rect.x));
  const top = Math.max(0, Math.floor(rect.y));
  const right = Math.min(size.width, Math.ceil(rect.x + rect.width));
  const bottom = Math.min(size.height, Math.ceil(rect.y + rect.height));
  for (let y = top; y < bottom; y += 1) {
    for (let x = left; x < right; x += 1) {
      const at = (y * size.width + x) * 4;
      data[at] = 255;
      data[at + 1] = 255;
      data[at + 2] = 255;
      data[at + 3] = 255;
    }
  }
  return { data, width: size.width, height: size.height };
}

/** Mean coverage 0..1 — how much of the layer the mask lets through. */
export function maskCoverage(mask: PixelBuffer): number {
  if (mask.width === 0 || mask.height === 0) return 0;
  let total = 0;
  for (let i = 3; i < mask.data.length; i += 4) total += mask.data[i] ?? 0;
  return total / (255 * mask.width * mask.height);
}

/** True when the mask shows everything: worth knowing before baking one in. */
export function maskRevealsAll(mask: PixelBuffer): boolean {
  for (let i = 3; i < mask.data.length; i += 4) {
    if ((mask.data[i] ?? 0) !== 255) return false;
  }
  return true;
}

/** True when the mask hides everything, i.e. the layer is invisible. */
export function maskHidesAll(mask: PixelBuffer): boolean {
  for (let i = 3; i < mask.data.length; i += 4) {
    if ((mask.data[i] ?? 0) !== 0) return false;
  }
  return true;
}

/**
 * Which layers can carry a mask.
 *
 * Only layers that own a raster of their own: the mask is sized to that raster,
 * and the paint, transform and merge paths already know how to move it around.
 * Text and fill layers are described by their parameters, not their pixels, so
 * masking one would mean rasterising it first — which is a decision the user
 * should make explicitly, not one a mask does silently.
 */
export function canHaveMask(layer: Layer | undefined): boolean {
  return layer?.type === 'image' || layer?.type === 'paint';
}
